import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  Box,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import ViewNavigation from "./ViewNavigation";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { useLocation } from "react-router-dom";
import PageRoute from "../../constant/pageRouteDictionary.json";


const resolveMenuTitle = ({
  location,
  currentPage,
  menuItems,
  PageRoute,
}) => {
  
  const pathname = location?.pathname;

  const hasValidCurrentPage =
    currentPage?.page || currentPage?.name;

  const fallbackMenuItem = menuItems?.find(menu => {
    const route = PageRoute?.[menu?.page];
    return route === pathname;
  });

  const resolvedMenuItem = hasValidCurrentPage
    ? currentPage
    : fallbackMenuItem;

  const title = resolvedMenuItem?.name;

  return {
    pathname,
    currentPage,
    menuItems,
    fallbackMenuItem,
    resolvedMenuItem,
    title,
  };
};


const IncomingOrdersHeader = ({
  hitsLabel,
  debouncedSearch,
  views,
  groups,
  activeViewId,
  openViewGroupId,
  openViewGroupAnchorEl,
  pinnedViewGroupId,
  addMenuAnchor,
  onAddMenuOpen,
  onAddMenuClose,
  onCreateViewClick,
  onCreateGroupClick,
  onGroupClick,
  onGroupHover,
  onNavigationLeave,
  onFloatingEnter,
  onFloatingLeave,
  onCloseFloating,
  onViewSelect,
  onEditGroupClick,
  onEditViewClick
}) => {
  const intl = useIntl();

  const location = useLocation();

  const currentPage = useSelector(state => state.ui.currentPage);

  const menuItems = useSelector(
    state => state.api.currentViewer.viewer.menuItems
  );

  const result = resolveMenuTitle({
    location,
    currentPage,
    menuItems,
    PageRoute,
  });

  const { title } = result;

  return (
  <Box sx={{ mb: 0 }}>
    <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0 }}>
      <Typography
        variant="h4"
        sx={{
          fontSize: 26,
          fontWeight: 500,
          letterSpacing: "-0.4px",
          color: "text.primary",
          lineHeight: 1.2,
        }}
        >
          {title ?? <FormattedMessage id="navMenu.incoming" />}
      </Typography>

      <Tooltip title={`${hitsLabel}${debouncedSearch ? ` · ${intl.formatMessage({ id: "incomingOrders.search.tooltip" }, { search: debouncedSearch })}` : ""}`}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.25,
            py: "3px",
            borderRadius: "10px",
            backgroundColor: "rgba(0,0,0,0.02)",
            fontSize: 12,
            fontWeight: 500,
            color: "text.secondary",
            userSelect: "none",
            cursor: "default",
          }}
        >
          {hitsLabel}
        </Box>
      </Tooltip>
    </Stack>

    <ViewNavigation
        groups={groups}
        views={views}
        activeViewId={activeViewId}
        openGroupId={openViewGroupId}
        openGroupAnchorEl={openViewGroupAnchorEl}
        pinnedGroupId={pinnedViewGroupId}
        onAddMenuOpen={onAddMenuOpen}
        onGroupClick={onGroupClick}
        onGroupHover={onGroupHover}
        onNavigationLeave={onNavigationLeave}
        onFloatingEnter={onFloatingEnter}
        onFloatingLeave={onFloatingLeave}
        onCloseFloating={onCloseFloating}
        onViewSelect={onViewSelect}

        onEditGroupClick={onEditGroupClick}
        onEditViewClick={onEditViewClick} 
    />

    <Menu
      anchorEl={addMenuAnchor}
      open={Boolean(addMenuAnchor)}
      onClose={onAddMenuClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        elevation: 4,
        sx: {
          mt: 0.75,
          minWidth: 160,
          borderRadius: "10px",
          border: "1px solid",
          borderColor: "divider",
          py: 0.5,
          boxShadow: "0 8px 28px rgba(0,0,0,0.14)",
        },
      }}
    >
      <MenuItem dense onClick={onCreateViewClick}>
        <VisibilityRoundedIcon sx={{ mr: 1.25, fontSize: 17, color: "text.secondary" }} />
        <ListItemText
          primary={intl.formatMessage({ id: "incomingOrders.header.createView" })}
          primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
          secondaryTypographyProps={{ fontSize: 11 }}
        />
      </MenuItem>

      <MenuItem dense onClick={onCreateGroupClick}>
        <FolderRoundedIcon sx={{ mr: 1.25, fontSize: 17, color: "text.secondary" }} />
        <ListItemText
          primary={intl.formatMessage({ id: "incomingOrders.header.createGroup" })}
          primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
          secondaryTypographyProps={{ fontSize: 11 }}
        />
      </MenuItem>
    </Menu>
  </Box>
  );
};

export default IncomingOrdersHeader;
